// Delete controls for sources, steps and outputs. Each asks for confirmation
// first. Without children they render a small trash icon.
import { Popconfirm } from "antd";
import { Trash2 } from "lucide-react";
import { getStepDefinition } from "../../../engine";
import { useBuilder } from "./BuilderContext";

const ConfirmDelete = ({ title, description, onConfirm, className = "", children }) => (
  // Stops clicks (also those inside the popup) from selecting the card or row underneath.
  <span className={`nodrag nopan ${className}`} onClick={(event) => event.stopPropagation()}>
    <Popconfirm title={title} description={description} okText="Delete" okButtonProps={{ danger: true }} onConfirm={onConfirm}>
      {children || (
        <button type="button" aria-label="Delete" className="flex rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600">
          <Trash2 size={13} />
        </button>
      )}
    </Popconfirm>
  </span>
);

export const DeleteStep = ({ step, children, className }) => {
  const { state, dispatch } = useBuilder();
  const index = state.workflow.steps.findIndex((s) => s.id === step.id);
  return (
    <ConfirmDelete
      className={className}
      title={`Delete step ${index + 1} · ${step.label || getStepDefinition(step.type)?.label || step.type}?`}
      description="Anything that reads from this step will read from its input instead. You can undo this."
      onConfirm={() => dispatch({ type: "removeStep", id: step.id })}
    >
      {children}
    </ConfirmDelete>
  );
};

export const DeleteSource = ({ source, children, className }) => {
  const { dispatch } = useBuilder();
  return (
    <ConfirmDelete
      className={className}
      title={`Delete source "${source.label}"?`}
      description="This can't be undone. Steps and outputs that read from it will need to be connected to a new source."
      onConfirm={() => dispatch({ type: "removeSource", id: source.id })}
    >
      {children}
    </ConfirmDelete>
  );
};

export const DeleteOutput = ({ output, children, className }) => {
  const { dispatch } = useBuilder();
  return (
    <ConfirmDelete
      className={className}
      title={`Delete output "${output.fileName}"?`}
      description="You can undo this."
      onConfirm={() => dispatch({ type: "removeOutput", id: output.id })}
    >
      {children}
    </ConfirmDelete>
  );
};

// Show the icon only while hovering its `group/card` card or `group/row` row.
export const SHOW_ON_CARD_HOVER = "opacity-0 transition-opacity group-hover/card:opacity-100 focus-within:opacity-100";
export const SHOW_ON_ROW_HOVER = "opacity-0 transition-opacity group-hover/row:opacity-100 focus-within:opacity-100";
